import { useQuery } from '@tanstack/react-query';
import { InvoiceRow } from '@blisspointmedia/bpm-types/dist/BillingAutomation';
import { awaitJSON, BillingAutomationLambdaFetch } from '@src/utils/fetch-utils';
import { APPROVED_CIDS, FinanceRow } from './finance-constants';
import { toFinanceRows } from './finance-utils';

const fetchCidInvoices = async (cid: string): Promise<FinanceRow[]> => {
  const res = await BillingAutomationLambdaFetch('/fetchBillingInvoices', { params: { cid } });
  const { invoices } = await awaitJSON<{ invoices: InvoiceRow[] }>(res);
  return toFinanceRows(cid, invoices);
};

// Fans out one request per approved CID. A partial result would silently under-report bills to
// finance, so any failed CID fails the whole query.
export const useFinanceInvoices = (): ReturnType<typeof useQuery<FinanceRow[]>> =>
  useQuery({
    queryKey: ['financeInvoices'],
    queryFn: async (): Promise<FinanceRow[]> => {
      const results = await Promise.allSettled(APPROVED_CIDS.map(fetchCidInvoices));
      const failed = APPROVED_CIDS.filter((_, i) => results[i].status === 'rejected');
      if (failed.length > 0) throw new Error(`Failed to load invoices for: ${failed.join(', ')}`);
      return results.flatMap(r => (r.status === 'fulfilled' ? r.value : []));
    }
  });
